import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { Card, Button, Input } from '../../components/common/UI'
import { useAdminAuth } from '../../hooks/useAdminAuth'
import { validators } from '../../utils/validation'

export default function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loginAttempts, setLoginAttempts] = useState(0)
  const [isLocked, setIsLocked] = useState(false)
  const [lockTimer, setLockTimer] = useState(0)
  const { signIn, loading, isAuthenticated } = useAdminAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    if (isLocked && lockTimer > 0) {
      const timer = setTimeout(() => {
        setLockTimer(lockTimer - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (isLocked && lockTimer === 0) {
      setIsLocked(false)
      setLoginAttempts(0)
    }
  }, [isLocked, lockTimer])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!validators.email(formData.email)) {
      newErrors.email = 'Invalid email format'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (isLocked) {
      return
    }

    setErrors({})

    if (!validateForm()) {
      return
    }

    const result = await signIn(formData.email, formData.password)
    
    if (result.success) {
      setLoginAttempts(0)
      navigate('/admin/dashboard', { replace: true })
    } else {
      const newAttempts = loginAttempts + 1
      setLoginAttempts(newAttempts)

      if (newAttempts >= 5) {
        setIsLocked(true)
        setLockTimer(300)
        setErrors({ 
          general: 'Too many failed attempts. Account locked for 5 minutes.' 
        })
      } else {
        setErrors({ 
          general: result.error || 'Invalid email or password',
          attempts: `${5 - newAttempts} attempts remaining`
        })
      }
    }
  }

  const formatLockTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center py-12 px-4">
      <Card className="max-w-md w-full p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
          <p className="text-gray-600 mt-2">Sign in to access the admin panel</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {errors.general && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-600">{errors.general}</p>
                  {errors.attempts && (
                    <p className="text-xs text-red-500 mt-1">{errors.attempts}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isLocked && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Account Locked</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Try again in {formatLockTime(lockTimer)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={errors.email}
            disabled={loading || isLocked}
            autoComplete="email"
          />

          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={errors.password}
              disabled={loading || isLocked}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            loading={loading}
            disabled={isLocked}
          >
            {isLocked ? 'Account Locked' : 'Sign In'}
          </Button>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              Authorized personnel only. All access is monitored and logged.
            </p>
          </div>
        </form>
      </Card>
    </div>
  )
}